/**
 * Region-count helpers — "one geometry, one population, every number".
 *
 * Unit of count everywhere is observation RECORDS (a cluster pin = 1
 * record), matching the watershed pill.
 *
 * ONE membership definition (membersOfWatershed): trust the stamped
 * `context.huc12` when present — the backend stamps new records by
 * point-in-polygon at write time and its backfill re-stamps ALL legacy
 * records geometrically — and fall back to the local ray-cast ONLY when
 * the stamp is null (pre-backfill rows). Every number on the surface
 * (the popup's recorded total when obs_count is absent, "showing {K}",
 * the pill chip/sentence, the badges, the spotlight) is computed from
 * that single rule; the numbers may differ only by POPULATION (all
 * fetched records vs. the records actually rendered), never by
 * membership definition.
 *
 * The ray-cast here is the former explore.tsx PIP helper, moved without
 * behavior change so every consumer counts against identical geometry.
 */

export interface PolygonGeometry {
  type: string;
  coordinates: unknown;
}

/** Minimal Point-feature shape shared by observation features. */
export interface PointFeatureLike {
  geometry: { type: string; coordinates: unknown };
  properties?: unknown;
}

/** Minimal watershed-feature shape (Polygon/MultiPolygon + huc12). */
export interface WatershedFeatureLike {
  geometry: PolygonGeometry;
  properties?: unknown;
}

/**
 * Ray-casting point-in-polygon for GeoJSON Polygon | MultiPolygon. Walks
 * the outer ring(s); holes are not modeled because HUC-12 polygons from
 * USGS WBD are simple shapes (no enclosed-water holes that matter for
 * "which watershed am I in").
 *
 * Returns true iff (lat, lon) lies strictly inside (or on the boundary
 * of) any outer ring of `geom`.
 */
export function pointInPolygonGeometry(
  lat: number,
  lon: number,
  geom: { type: string; coordinates: unknown } | undefined,
): boolean {
  if (!geom) return false;
  const rings: number[][][] =
    geom.type === 'Polygon'
      ? [(geom.coordinates as number[][][])[0]]
      : geom.type === 'MultiPolygon'
        // Each polygon's outer ring (index 0). Ignore inner holes.
        ? (geom.coordinates as number[][][][]).map((poly) => poly[0])
        : [];
  for (const ring of rings) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const intersect =
        yi > lat !== yj > lat &&
        lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    if (inside) return true;
  }
  return false;
}

/** [lon, lat] of a Point feature, or null if malformed. */
function pointCoords(f: PointFeatureLike): [number, number] | null {
  const c = f.geometry?.coordinates;
  if (!Array.isArray(c) || c.length < 2) return null;
  const lon = Number(c[0]);
  const lat = Number(c[1]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  return [lon, lat];
}

/** Stamped `context.huc12` of an observation feature, or null. */
export function stampedHuc12(f: PointFeatureLike): string | null {
  const ctx = (f.properties as { context?: { huc12?: unknown } } | null | undefined)
    ?.context;
  const h = ctx?.huc12;
  return typeof h === 'string' && h.length > 0 ? h : null;
}

/**
 * Record count of a feature set. One feature = one observation RECORD
 * (a cluster pin = 1 record) — the single count unit shared by the
 * polygon popup, the pill chip/sentence, and the badges.
 */
export function countRecords(features: ArrayLike<unknown> | null | undefined): number {
  return features ? features.length : 0;
}

/**
 * THE membership rule: trust the stamped `context.huc12` when present;
 * fall back to the local ray-cast ONLY when the stamp is null. Applied
 * to the rendered records it yields K ("showing {K} on the map now")
 * and the spotlight member set; applied to ALL fetched records it
 * yields the recorded-total fallback when `properties.obs_count` is
 * absent from the watersheds payload — same rule, same numbers.
 */
export function membersOfWatershed<F extends PointFeatureLike>(
  features: readonly F[],
  huc12: string,
  geometry: PolygonGeometry | undefined,
): F[] {
  return features.filter((f) => {
    const stamped = stampedHuc12(f);
    if (stamped !== null) return stamped === huc12;
    const c = pointCoords(f);
    if (!c) return false;
    return pointInPolygonGeometry(c[1], c[0], geometry);
  });
}

/**
 * Count of features with a NULL huc12 stamp that nevertheless fall
 * inside one of the loaded watershed polygons — i.e. rows the backend
 * has not backfilled yet. Post-backfill this returns 0 and the chip it
 * feeds never renders. Recompute only on data-version change, not pan.
 */
export function countUnassignedInMapped(
  features: readonly PointFeatureLike[],
  watershedFeatures: readonly WatershedFeatureLike[],
): number {
  let n = 0;
  for (const f of features) {
    if (stampedHuc12(f) !== null) continue;
    const c = pointCoords(f);
    if (!c) continue;
    for (const w of watershedFeatures) {
      if (pointInPolygonGeometry(c[1], c[0], w.geometry)) {
        n++;
        break;
      }
    }
  }
  return n;
}

// ─── Watershed-popup copy (contract copy spec) ─────────────────────────

export interface WatershedPopupCopy {
  /**
   * Header count line — `"{n} litter report{s} recorded inside this
   * boundary"`, or the empty-state lead when nothing is recorded.
   */
  countLine: string;
  /** `"showing {K} on the map now"` when the map shows K ≠ recorded; else null. */
  showingLine: string | null;
  /** True when the empty-state ("be the first" + Report litter reference) renders. */
  isEmpty: boolean;
  /** `"one cluster near the boundary spans into {other}"` or null. */
  straddleLine: string | null;
  /** Basis footnote — always present. */
  basisLine: string;
}

/**
 * Pure copy builder for the polygon-click popup. `recordedTotal` is the
 * authoritative obs_count (or its fallback: membersOfWatershed over ALL
 * fetched records); `showingCount` is K = member records among the
 * features actually rendered on the map.
 */
export function buildWatershedPopupCopy(args: {
  recordedTotal: number;
  showingCount: number;
  straddleOtherName?: string | null;
}): WatershedPopupCopy {
  const { recordedTotal, showingCount } = args;
  const isEmpty = recordedTotal === 0;
  const countLine = isEmpty
    ? 'None recorded here yet — be the first'
    : `${recordedTotal} litter report${recordedTotal === 1 ? '' : 's'} recorded inside this boundary`;
  const showingLine =
    !isEmpty && showingCount !== recordedTotal
      ? `showing ${showingCount} on the map now`
      : null;
  const straddleLine = args.straddleOtherName
    ? `one cluster near the boundary spans into ${args.straddleOtherName}`
    : null;
  return {
    countLine,
    showingLine,
    isEmpty,
    straddleLine,
    basisLine:
      'Counts use map location; points within ~100 m of the boundary may fall on either side.',
  };
}
